import scrollToElement from "@/utils/scroll-to-element";
import { ID, play } from "@voomp/play-back";
import { defineStore } from "pinia";
import { ref } from "vue";
import { useAuthStore } from "@/store/auth.store";

export const useHomeStore = defineStore('home', () => {

	const site = ref<Awaited<ReturnType<typeof play.home.public>>>({
		settings: {
			view: "COURSE",
			mode: "CAROUSEL",
			cover_size: "SMALL",
			cover_orientation: "VERTICAL",
			cover: "small_vertical",
			nav_category: false,
		},
		courses: [],
		categories: [],
		banners_top: [],
		banners_bottom: [],
	})

	const member = ref<Awaited<ReturnType<typeof play.home.member>>>({
		categories: {},
		courses: {},
		modules: {},
	})

	const seller = ref<Awaited<ReturnType<typeof play.home.seller>>>({
		courses: {},
		modules: {},
	})

	const globalLoading = ref({
		global: false,
		member: false,
		seller: true,
	})

	const loaded = ref({
		global: false,
		member: false,
		seller: false,
	})

	const selectedCategoryId = ref<ID>(0)
	const isRearranging = ref(false)
	const pendingOrderByCategory = ref<Record<string | number, number[]>>({})


	async function fetch() {
		const authStore = useAuthStore();
		if (!authStore.getMemberId() || !authStore.getSiteId()) return;
		try {
			globalLoading.value.global = true;
			const resp = await play.home.public({});
			site.value = resp;
		} catch (error) {
			console.error("Erro ao buscar dados:", error);
		} finally {
			globalLoading.value.global = false;
			loaded.value.global = true;
		}
	}

	async function fetchMember() {
		const authStore = useAuthStore();
		if (!authStore.getMemberId() || !authStore.getSiteId()) return;
		try {
			globalLoading.value.member = true;
			const resp = await play.home.member({});
			member.value = resp;
		} catch (error) {
			console.error("Erro ao buscar dados:", error);
		} finally {
			globalLoading.value.member = false;
			loaded.value.member = true;
		}
	}

	async function fetchSeller() {
		try {
			globalLoading.value.seller = true;
			const resp = await play.home.seller({});
			seller.value = resp;
		} catch (error) {
			console.error("Erro ao buscar dados:", error);
		} finally {
			globalLoading.value.seller = false;
			loaded.value.seller = true;
		}
	}

	function openCategory(categoryId: ID) {
		requestAnimationFrame(() => {
			scrollToElement(`accordion-${categoryId}`);
		});
		selectedCategoryId.value = categoryId;
	}

	function getSiteCourses() {
		const courses = site.value.courses;
		return Array.isArray(courses) ? courses : Object.values(courses as Record<string, unknown>);
	}

	function getCourse(courseId: ID) {
		const course = getSiteCourses().find(course => course.id == courseId);
		return course;
	}

	function getMemberCourse(courseId: ID) {
		const course = member.value.courses[courseId];
		return course;
	}

	function getMemberModule(moduleId: ID) {
		const module = member.value.modules?.[moduleId];
		return module;
	}
	
	function getAccessibleCategories() {
		const accessibleCategoriesIds = Object.keys(member.value.categories);
		return Object.values(site.value.categories).filter(category =>
			accessibleCategoriesIds.some(e => Number(e) == category.id)
		);
	}

	function getMemberVisibleCourses() {
		const authStore = useAuthStore();
		const memberCoursesIds = Object.keys(member.value.courses);
		return getSiteCourses().filter(course =>
			memberCoursesIds.some(memberCourse => course.id == Number(memberCourse)) &&
			(authStore.isAdmin() || (course as Record<string, unknown>).status === 'published')
		);
	}

	function hasAccessCourse(courseId: ID) {
		return !!getMemberCourse(courseId)?.has_access;
	}

	function isCourseLiberated(courseId: ID) {
		return !!getMemberCourse(courseId)?.is_liberated;
	}

	function isModuleLiberated(moduleId: ID) {
		return !!getMemberModule(moduleId)?.is_liberated;
	}

	function getTotalStudentsOnCourse(courseId: ID) {
		return seller.value?.courses?.[courseId]?.students_count || 0;
	}

	function getDraftLessonsCountOnCourse(courseId: ID) {
		return seller.value?.courses?.[courseId]?.draft_lessons_count || 0;
	}

	function getDraftLessonsCountOnModule(moduleId: ID) {
		return seller.value?.modules?.[moduleId]?.draft_lessons_count || 0;
	}

	function getStudentProgress(courseId: ID) {
		return member.value.courses[courseId]?.progress || 0;
	}

	function getVisibleModules(courseId: ID) {
		const authStore = useAuthStore();
		const course = getCourse(courseId);
		if (!course?.modules) return [];
		if (authStore.isAdmin()) return course.modules;
		return course.modules.filter(m => !m.status || m.status === 'published');
	}

	function getCategoryCourses(categoryId: ID) {
		return getMemberVisibleCourses().filter(course => course.category_id == categoryId);
	}

	function toggleRearrange() {
		isRearranging.value = !isRearranging.value;
		if (!isRearranging.value) pendingOrderByCategory.value = {};
	}

	function setPendingOrder(categoryId: string | number, courseIds: number[]) {
		pendingOrderByCategory.value[categoryId] = courseIds;
	}

	async function saveRearrange() {
		const allIds = site.value.categories.flatMap(cat =>
			pendingOrderByCategory.value[cat.id] ??
			getSiteCourses().filter((c: Record<string, unknown>) => c.category_id === cat.id).map((c: Record<string, unknown>) => c.id)
		);
		await play.course.update_order(allIds);
		pendingOrderByCategory.value = {};
		isRearranging.value = false;
		await fetch();
	}

	function cancelRearrange() {
		pendingOrderByCategory.value = {};
		isRearranging.value = false;
	}

	function $reset() {
		site.value = {
			settings: {
				view: "COURSE",
				mode: "CAROUSEL",
				cover_size: "SMALL",
				cover_orientation: "VERTICAL",
				cover: "small_vertical",
				nav_category: false,
			},
			courses: [],
			categories: [],
			banners_top: [],
			banners_bottom: [],
		}
		member.value = {
			categories: {},
			courses: {},
			modules: {},
		}
		seller.value = {
			courses: {},
			modules: {},
		}
		globalLoading.value = {
			global: false,
			member: false,
			seller: true,
		}
		loaded.value = {
			global: false,
			member: false,
			seller: false,
		}
		selectedCategoryId.value = 0
		isRearranging.value = false
	}

	return {
		site,
		member,
		seller,
		globalLoading,
		loaded,
		selectedCategoryId,
		isRearranging,
		fetch,
		fetchMember,
		fetchSeller,
		openCategory,
		getCourse,
		getMemberCourse,
		getMemberModule,
		getAccessibleCategories,
		getMemberVisibleCourses,
		hasAccessCourse,
		isCourseLiberated,
		isModuleLiberated,
		getTotalStudentsOnCourse,
		getDraftLessonsCountOnCourse,
		getDraftLessonsCountOnModule,
		getStudentProgress,
		getVisibleModules,
		getCategoryCourses,
		toggleRearrange,
		setPendingOrder,
		saveRearrange,
		cancelRearrange,
		$reset,
	}
});
